/* eslint-disable no-unused-vars */
import { useCallback, useRef } from 'react';
import supabase, { orgId } from '../supabaseClient';
import { A } from '../utils/format';
import { PRE_MONTHS, TOTAL_COST_MONTHS } from '../utils/constants';

const RFIELDS = ['units','regionalPct','discountPct','daysDiscount','platformDeal','resellers','physical','otherDeal','customRow1','customRow2'];
const RFIELDS_STR = ['pricingMode','dlcs']; // string fields stored as-is
const RMAP = {
  units: 'units', regionalPct: 'regional_pct', discountPct: 'discount_pct',
  platformDeal: 'platform_deal', customRow1: 'custom_row1', customRow2: 'custom_row2',
};
const CFIELDS = ['devAdvances','loc','qa','otherPub','marketing'];
const CMAP = { devAdvances:'dev_advances', loc:'loc', qa:'qa', otherPub:'other_pub', marketing:'marketing' };

const _gidCache = {};

async function getUID() {
  const { data } = await supabase.auth.getUser();
  return data?.user?.id || null;
}

async function getOrCreateGID(uid, name, order) {
  const cacheKey = `${orgId}:${name}`;
  if (_gidCache[cacheKey]) return _gidCache[cacheKey];
  const { data } = await supabase.from('games').select('id')
    .eq('org_id', orgId).eq('name', name).maybeSingle();
  if (data?.id) { _gidCache[cacheKey] = data.id; return data.id; }
  const { data: ins } = await supabase.from('games')
    .insert({ org_id: orgId, user_id: uid, name, sort_order: order }).select('id').single();
  if (ins?.id) { _gidCache[cacheKey] = ins.id; return ins.id; }
  return null;
}

export async function syncToSupabase(games, PS, CS, DT) {
  const uid = await getUID();
  if (!uid) return;

  // Delete games removed from the list
  const { data: existingGames } = await supabase.from('games').select('id, name').eq('org_id', orgId);
  const currentNames = new Set(games);
  for (const eg of (existingGames || [])) {
    if (!currentNames.has(eg.name)) {
      await supabase.from('games').delete().eq('id', eg.id);
      delete _gidCache[`${orgId}:${eg.name}`];
    }
  }

  const gameIds = {};
  for (let i = 0; i < games.length; i++) {
    const gid = await getOrCreateGID(uid, games[i], i);
    if (gid) gameIds[games[i]] = gid;
  }

  // Deal terms
  for (const gn of games) {
    const gid = gameIds[gn]; if (!gid) continue;
    const dt = DT[gn]; if (!dt) continue;
    await supabase.from('deal_terms').upsert({
      game_id: gid, user_id: uid, org_id: orgId,
      dev_advance_currency: dt.devAdvanceCurrency || 'USD',
      dev_advance_amt: parseFloat(dt.devAdvanceAmt) || null,
      pub_costs_budget: parseFloat(dt.pubCostsBudget) || null,
      marketing_budget: parseFloat(dt.marketingBudget) || null,
      royalty_pre_recoup: parseFloat(dt.royaltyPreRecoup) || null,
      royalty_post_recoup: parseFloat(dt.royaltyPostRecoup) || null,
    }, { onConflict: 'game_id' });
  }

  // Delete all existing monthly data for these games, then re-upsert current state
  // This ensures deleted/zeroed values are properly removed
  if (Object.keys(gameIds).length > 0) {
    const allGids = Object.values(gameIds);
    await supabase.from('monthly_data').delete().in('game_id', allGids);
  }

  // Platform config + monthly data
  const mRows = [];
  for (const gn of games) {
    const gid = gameIds[gn]; if (!gid) continue;
    const pm = PS[gn] || {}, cm = CS[gn] || {};

    for (const [pk, pe] of Object.entries(pm)) {
      await supabase.from('platform_config').upsert({
        game_id: gid, user_id: uid, org_id: orgId, platform_key: pk, enabled: pe.enabled,
        rrp_ea: parseFloat(pe.rrpEA) || null, rrp_fl: parseFloat(pe.rrpFL) || null,
        launch_ea: pe.launchEA !== '' ? parseInt(pe.launchEA) : null,
        launch_fl: pe.launchFL !== '' ? parseInt(pe.launchFL) : null,
        units_method: pe.method,
        custom_row1_label: pe.customRow1Label, custom_row2_label: pe.customRow2Label,
      }, { onConflict: 'game_id,platform_key' });

      for (const f of RFIELDS) {
        (pe[f] || []).forEach((v, mi) => {
          const n = parseFloat(v);
          if (!isNaN(n) && n !== 0)
            mRows.push({ game_id: gid, user_id: uid, org_id: orgId, platform_key: pk, field: RMAP[f], month_index: mi, value: n });
        });
      }
    }

    const gc2 = cm['__game__'];
    if (gc2) {
      for (const sec of ['recoup', 'nonRecoup']) {
        const pfx = sec === 'recoup' ? 'recoup_' : 'nonrecoup_';
        for (const f of CFIELDS) {
          (gc2[sec][f] || []).forEach((v, arrayIdx) => {
            const n = parseFloat(v);
            // Convert array index to month index (array index 36 = month 0, index 0 = month -36)
            const monthIdx = arrayIdx - PRE_MONTHS;
            if (!isNaN(n) && n !== 0)
              mRows.push({ game_id: gid, user_id: uid, org_id: orgId, platform_key: '__game__', field: pfx + CMAP[f], month_index: monthIdx, value: n });
          });
        }
      }
    }
  }

  if (mRows.length > 0) {
    for (let i = 0; i < mRows.length; i += 500) {
      await supabase.from('monthly_data').upsert(mRows.slice(i, i + 500), {
        onConflict: 'game_id,platform_key,field,month_index',
      });
    }
  }
}

export async function loadFromSupabase() {
  const uid = await getUID();
  if (!uid) return null;

  const { data: gd } = await supabase.from('games')
    .select('id,name,sort_order').eq('org_id', orgId).order('sort_order');
  if (!gd?.length) return null;

  const games = gd.map(g => g.name);
  gd.forEach(g => { _gidCache[`${orgId}:${g.name}`] = g.id; });
  const ids = gd.map(g => g.id);

  const PS = {}, CS = {}, DT = {};

  const { data: dt } = await supabase.from('deal_terms').select('*').in('game_id', ids);
  for (const r of dt || []) {
    const n = gd.find(g => g.id === r.game_id)?.name; if (!n) continue;
    DT[n] = {
      devAdvanceCurrency: r.dev_advance_currency, devAdvanceAmt: r.dev_advance_amt?.toString() || '',
      pubCostsBudget: r.pub_costs_budget?.toString() || '', marketingBudget: r.marketing_budget?.toString() || '',
      royaltyPreRecoup: r.royalty_pre_recoup?.toString() || '', royaltyPostRecoup: r.royalty_post_recoup?.toString() || '',
    };
  }

  const { data: pc } = await supabase.from('platform_config').select('*').in('game_id', ids);
  for (const r of pc || []) {
    const n = gd.find(g => g.id === r.game_id)?.name; if (!n) continue;
    if (!PS[n]) PS[n] = {};
    PS[n][r.platform_key] = {
      enabled: r.enabled, rrpEA: r.rrp_ea?.toString() || '', rrpFL: r.rrp_fl?.toString() || '',
      launchEA: r.launch_ea?.toString() || '', launchFL: r.launch_fl?.toString() || '',
      method: r.units_method,
      units: A(), regionalPct: A('0'), discountPct: A('0'), platformDeal: A('0'),
      customRow1Label: r.custom_row1_label, customRow1: A('0'),
      customRow2Label: r.custom_row2_label, customRow2: A('0'),
    };
  }

  const RREV = { units:'units', regional_pct:'regionalPct', discount_pct:'discountPct', platform_deal:'platformDeal', custom_row1:'customRow1', custom_row2:'customRow2' };
  const CREV = {
    recoup_dev_advances:{s:'recoup',f:'devAdvances'}, recoup_loc:{s:'recoup',f:'loc'},
    recoup_qa:{s:'recoup',f:'qa'}, recoup_other_pub:{s:'recoup',f:'otherPub'}, recoup_marketing:{s:'recoup',f:'marketing'},
    nonrecoup_dev_advances:{s:'nonRecoup',f:'devAdvances'}, nonrecoup_loc:{s:'nonRecoup',f:'loc'},
    nonrecoup_qa:{s:'nonRecoup',f:'qa'}, nonrecoup_other_pub:{s:'nonRecoup',f:'otherPub'}, nonrecoup_marketing:{s:'nonRecoup',f:'marketing'},
  };

  const { data: md } = await supabase.from('monthly_data').select('*').in('game_id', ids);
  for (const r of md || []) {
    const n = gd.find(g => g.id === r.game_id)?.name; if (!n) continue;
    const { platform_key: pk, field: fld, month_index: mi, value } = r;
    const val = value?.toString() || '';
    if (RREV[fld]) { if (PS[n]?.[pk]) PS[n][pk][RREV[fld]][mi] = val; continue; }
    if (!CS[n]) CS[n] = {};
    if (!CS[n][pk]) CS[n][pk] = { recoup: { devAdvances:Array(TOTAL_COST_MONTHS).fill(''),loc:Array(TOTAL_COST_MONTHS).fill(''),qa:Array(TOTAL_COST_MONTHS).fill(''),otherPub:Array(TOTAL_COST_MONTHS).fill(''),marketing:Array(TOTAL_COST_MONTHS).fill('') }, nonRecoup: { devAdvances:Array(TOTAL_COST_MONTHS).fill(''),loc:Array(TOTAL_COST_MONTHS).fill(''),qa:Array(TOTAL_COST_MONTHS).fill(''),otherPub:Array(TOTAL_COST_MONTHS).fill(''),marketing:Array(TOTAL_COST_MONTHS).fill('') } };
    const m = CREV[fld]; 
    if (m) {
      // Convert month index to array index (month -36 → array 0, month 0 → array 36)
      const arrayIdx = mi + PRE_MONTHS;
      if (arrayIdx >= 0 && arrayIdx < TOTAL_COST_MONTHS) {
        if (!CS[n][pk]) CS[n][pk] = { recoup: { devAdvances: Array(TOTAL_COST_MONTHS).fill(''), loc: Array(TOTAL_COST_MONTHS).fill(''), qa: Array(TOTAL_COST_MONTHS).fill(''), otherPub: Array(TOTAL_COST_MONTHS).fill(''), marketing: Array(TOTAL_COST_MONTHS).fill('') }, nonRecoup: { devAdvances: Array(TOTAL_COST_MONTHS).fill(''), loc: Array(TOTAL_COST_MONTHS).fill(''), qa: Array(TOTAL_COST_MONTHS).fill(''), otherPub: Array(TOTAL_COST_MONTHS).fill(''), marketing: Array(TOTAL_COST_MONTHS).fill('') } };
        CS[n][pk][m.s][m.f][arrayIdx] = val;
      }
    }
  }

  try { localStorage.removeItem('pfm'); } catch (_) {}
  return { games, PS, CS, DT };
}

export function useSync() {
  const timerRef = useRef(null);
  const sSync = useCallback((games, PS, CS, DT, onSaved) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try { localStorage.setItem('pfm', JSON.stringify({ games, PS, CS, DT })); } catch (_) {}
      try { await syncToSupabase(games, PS, CS, DT); } catch (e) { console.error('[sync]', e); }
      onSaved?.();
    }, 800);
  }, []);
  return sSync;
}
